import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, RefreshCw, UserPlus } from "lucide-react";
import { api } from "../../lib/api";
import { enrolQuestion, LIMO_STATES } from "../../lib/limoGroup";
import { ACCESS_FILTERS, accessSummary, matchesFilter } from "../../lib/limoGroupAccess";
import LimoAccessRow from "./LimoAccessRow";

/**
 * The cohort's participants and where each stands with Logiscool, and the
 * one button that enrols everyone ready — signed contract, not enrolled yet
 * (decided 22.09.2026). The list doubles as the preview: who goes, how many
 * licences it costs, how many are left.
 */
export default function LimoGroupParticipants({ groupId, sandbox, canMutate }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  // The access side (08.10.2026): every enrolled participant's account, read
  // in one call; which row has its details open; the filter above the list.
  const [access, setAccess] = useState(null);
  const [open, setOpen] = useState(null);
  const [filter, setFilter] = useState("all");

  const loadAccess = useCallback(async () => {
    try {
      const r = await api.get(`/admin/groups/${groupId}/limo/accounts`, { params: { sandbox } });
      setAccess(r.data);
    } catch {
      setAccess({ ok: false });
    }
  }, [groupId, sandbox]);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/admin/groups/${groupId}/limo/participants`, { params: { sandbox } });
      setData(r.data);
    } catch (err) {
      setData({ error: err?.response?.data?.detail || "Nu am putut citi participanții." });
    }
  }, [groupId, sandbox]);

  const reloadAll = useCallback(async () => {
    await Promise.all([load(), loadAccess()]);
  }, [load, loadAccess]);

  useEffect(() => {
    setData(null); setResult(null); setAccess(null); setOpen(null); setFilter("all");
    load();
    loadAccess();
    return () => setOpen(null);
  }, [load, loadAccess]);

  const enrol = async () => {
    if (!window.confirm(enrolQuestion(data, sandbox))) return;
    setBusy(true);
    try {
      const r = await api.post(`/admin/groups/${groupId}/limo/enroll`, null, { params: { sandbox } });
      setResult(r.data);
      const n = r.data.enrolled.length;
      if (n) toast.success(`${n} ${n === 1 ? "participant înscris" : "participanți înscriși"} în Logiscool.`);
      if (r.data.stopped) toast.error(r.data.stopped);
      await reloadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Înscrierea nu a reușit.");
    } finally {
      setBusy(false);
    }
  };

  const moveOne = async (it) => {
    if (!window.confirm(`Îl muți pe ${it.name} în această grupă și la Logiscool? Întâi e adăugat aici, apoi scos din `
      + "grupa veche; nu se consumă nicio licență în plus.")) return;
    setBusy(true);
    try {
      const r = await api.post(`/admin/enrollments/${it.enrollment_id}/limo/move`, null, { params: { sandbox } });
      if (r.data.partial) toast.warning(r.data.message); else toast.success(r.data.message);
      await reloadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Mutarea nu a reușit.");
    } finally {
      setBusy(false);
    }
  };

  if (!data) {
    return <div className="mt-3 flex items-center gap-2 text-xs text-[#6B7280]"><Loader2 size={13} className="animate-spin" /> Se încarcă participanții…</div>;
  }
  if (data.error) return <p className="mt-3 text-xs text-[#9b1f2c]">{data.error}</p>;

  const ready = data.counts.ready;
  const free = data.capacity?.free;

  return (
    <div className="mt-3 rounded-xl border border-[#E5E7EB] bg-white p-3" data-testid="group-limo-participants">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm font-semibold text-[#373A45]">Participanți</div>
        <div className="flex items-center gap-2 text-[11px] text-[#6B7280]">
          {typeof free === "number" && <span>licențe libere: <strong className="text-[#373A45] tabular-nums">{free}</strong></span>}
          <button type="button" onClick={reloadAll} title="Reîncarcă lista și starea conturilor de la Logiscool"
            className="inline-flex items-center gap-1 rounded-full px-2 py-1 hover:bg-[#F5F6F6]" aria-label="Reîncarcă starea"
            data-testid="group-limo-reload">
            <RefreshCw size={12} /> Reîncarcă starea
          </button>
        </div>
      </div>

      <div className="mt-2 flex flex-wrap gap-1.5 text-[11px]">
        {Object.entries(LIMO_STATES).map(([key, s]) => (
          <span key={key} className={`rounded-full px-2 py-0.5 font-semibold ${s.cls}`}>
            {s.label}: <span className="tabular-nums">{data.counts[key]}</span>
          </span>
        ))}
      </div>

      {access && (access.ok === false ? (
        <p className="mt-2 text-[11px] text-[#9b1f2c]">Nu am putut citi starea conturilor de la Logiscool. Încearcă „Reîncarcă starea”.</p>
      ) : Object.keys(access.accounts || {}).length > 0 && (
        <div className="mt-2 rounded-lg bg-[#F5F6F6] px-3 py-2" data-testid="group-limo-access-summary">
          <div className="text-[11px] font-semibold text-[#373A45]">{accessSummary(access.counts)}</div>
          <div className="mt-1.5 flex flex-wrap gap-1">
            {ACCESS_FILTERS.map((f) => (
              <button key={f.key} type="button" onClick={() => setFilter(f.key)}
                className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${filter === f.key
                  ? "bg-[#373A45] text-white" : "bg-white text-[#373A45] border border-[#E5E7EB] hover:border-[#965EA7]"}`}
                data-testid={`group-limo-filter-${f.key}`}>
                {f.label}
              </button>
            ))}
          </div>
        </div>
      ))}
      {access === null && (
        <div className="mt-2 flex items-center gap-1.5 text-[11px] text-[#6B7280]">
          <Loader2 size={11} className="animate-spin" /> Se citește starea conturilor…
        </div>
      )}

      {data.items.length === 0 ? (
        <p className="mt-2 text-xs text-[#6B7280]">Niciun participant plătit în această grupă.</p>
      ) : (
        <ul className="mt-2 max-h-[32rem] divide-y divide-[#E5E7EB] overflow-y-auto text-xs" data-testid="group-limo-participant-list">
          {data.items.filter((it) => matchesFilter(it, access?.accounts?.[it.enrollment_id], filter)).map((it) => {
            const s = LIMO_STATES[it.state];
            const account = access?.accounts?.[it.enrollment_id];
            return (
              <li key={it.enrollment_id} className="flex flex-wrap items-center justify-between gap-x-2 py-1.5">
                <span className="min-w-0">
                  <span className="font-medium text-[#373A45]">{it.name}</span>
                  <span className="ml-1.5 text-[#6B7280]">{it.detail}</span>
                </span>
                <span className="flex shrink-0 items-center gap-1.5">
                  {it.state === "to_move" && canMutate && (
                    <button type="button" onClick={() => moveOne(it)} disabled={busy}
                      className="rounded-full border border-[#6B3F7A]/30 bg-white px-2 py-0.5 text-[10px] font-semibold text-[#6B3F7A] hover:bg-[#F5F0F8] disabled:opacity-50"
                      data-testid={`group-limo-move-${it.enrollment_id}`}>
                      Mută aici
                    </button>
                  )}
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${s.cls}`}>{s.label}</span>
                </span>
                {account && (
                  <LimoAccessRow item={it} account={account} sandbox={sandbox} free={free}
                    busy={busy} setBusy={setBusy} open={open} setOpen={setOpen} onChanged={reloadAll} />
                )}
              </li>
            );
          })}
        </ul>
      )}
      {filter !== "all" && data.items.length > 0
        && !data.items.some((it) => matchesFilter(it, access?.accounts?.[it.enrollment_id], filter)) && (
        <p className="mt-2 text-xs text-[#6B7280]">Nimeni din grupă la acest filtru.</p>
      )}

      {canMutate && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <button type="button" onClick={enrol} disabled={busy || !ready}
            className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-semibold text-white disabled:opacity-50 ${
              sandbox ? "bg-[#519872] hover:bg-[#3f7a5c]" : "bg-[#965EA7] hover:bg-[#7A4986]"}`}
            data-testid="group-limo-enroll">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <UserPlus size={14} />}
            {sandbox ? "Înscrie de probă" : "Înscrie în Logiscool"} ({ready} gata)
          </button>
          <span className="text-[11px] text-[#6B7280]">
            Doar cei cu contractul semnat. Fiecare primește pe email datele de acces.
          </span>
        </div>
      )}

      {result && (result.failed.length > 0 || result.stopped) && (
        <div className="mt-2 rounded-lg bg-[#FDECEC] px-3 py-2 text-xs text-[#9b1f2c]" data-testid="group-limo-enroll-result">
          {result.stopped && <p>{result.stopped} Au rămas {result.remaining} de înscris.</p>}
          {result.failed.map((f) => <p key={f.enrollment_id}>{f.name}: {f.error}</p>)}
        </div>
      )}
    </div>
  );
}
